import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import type { DeepUnknown } from '@/types/deep-unknown';

import { TaskSchema } from '../entities/task';
import { WorkstreamSchema } from '../entities/workstream';

export namespace WorkstreamQuery {
  export const key = (input: Input) => [
    'project-management',
    'workstream',
    input.id,
  ];

  export type Input = { id: string };
  export type Params = { enabled?: boolean };

  export const OutputSchema = z.object({
    workstream: WorkstreamSchema.pick({
      id: true,
      name: true,
      description: true,
      startDate: true,
      endDate: true,
    }).extend({
      tasks: z.array(
        TaskSchema.pick({
          id: true,
          title: true,
        }),
      ),
    }),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(
    data: FeApi.GetWorkstreamViewOutput,
  ): DeepUnknown<Output> {
    return {
      workstream: {
        id: data.uuid,
        name: data.name,
        description: data.description,
        startDate: new Date(data.start_date),
        endDate: new Date(data.end_date),
        tasks: data.cards.map((card) => ({
          id: card.uuid,
          title: card.title,
        })),
      },
    };
  }

  function useQueryFn() {
    const workstreamDetails = useInjectFeApiContext(
      FeApi.tasksV1WorkstreamsDetailsRetrieve,
    );

    return async function queryFn(input: Input) {
      const workstreamResult = await ok(
        workstreamDetails({
          uuid: input.id,
        }),
      );

      const output = await OutputSchema.parseAsync(
        outputMapper(workstreamResult),
      );

      return output;
    };
  }

  export function useQuery(input: Input, params?: Params) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      enabled: params?.enabled,
      queryKey: key(input),
      queryFn: () => queryFn(input),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
